import React, { useState, useRef, useMemo } from 'react';
import { ArrowLeft, Plus, Upload, Trash2, Edit2, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { Typography, Pagination, Checkbox } from 'antd';
import { Button as CrcButton } from '@crc-evans/ui-components';
import * as xlsx from 'xlsx';
import MasterDataModal from './MasterDataModal';

const { Title } = Typography;

const MobilistMasterDataView = ({ data, onUpdateData, onBack }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Search and Sort state
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const fileInputRef = useRef(null);

  // ── Data Processing (Filter & Sort) ──
  const processedData = useMemo(() => {
    let result = [...data];

    // 1. Column Search
    const activeFilters = Object.entries(columnFilters).filter(([_, val]) => val);
    if (activeFilters.length > 0) {
      result = result.filter(item => {
        return activeFilters.every(([key, val]) => {
          const itemVal = item[key];
          return itemVal && String(itemVal).toLowerCase().includes(val.toLowerCase());
        });
      });
    }

    // 2. Sort
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';
        
        // Handle numeric sorting for Price
        if (sortConfig.key === '__EMPTY_1') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        } else {
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, columnFilters, sortConfig]);

  // Pagination derived state
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = processedData.slice(startIndex, startIndex + pageSize);

  // Formatting helpers
  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '—';
    const num = parseFloat(val);
    if (isNaN(num)) return val;
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(num);
  };

  // ── Selection Logic ──
  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      const pageIds = paginatedData.map(item => item.id);
      setSelectedIds(new Set([...selectedIds, ...pageIds]));
    } else {
      const newSelected = new Set(selectedIds);
      paginatedData.forEach(item => newSelected.delete(item.id));
      setSelectedIds(newSelected);
    }
  };

  const toggleSelectRow = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const isAllPageSelected = paginatedData.length > 0 && paginatedData.every(item => selectedIds.has(item.id));
  const isIndeterminate = paginatedData.some(item => selectedIds.has(item.id)) && !isAllPageSelected;

  // ── Actions ──
  const handleDeleteSelected = () => {
    const newData = data.filter(item => !selectedIds.has(item.id));
    onUpdateData(newData);
    setSelectedIds(new Set());
  };

  const handleDeleteRow = (id) => {
    const newData = data.filter(item => item.id !== id);
    onUpdateData(newData);
    
    if (selectedIds.has(id)) {
      const newSelected = new Set(selectedIds);
      newSelected.delete(id);
      setSelectedIds(newSelected);
    }
  };

  const handleEditRow = (item) => {
    setEditingItem(item);
    setIsModalVisible(true);
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setIsModalVisible(true);
  };

  const handleModalSave = (values) => {
    if (editingItem) {
      // Update
      const newData = data.map(item => item.id === editingItem.id ? { ...values, id: item.id } : item);
      onUpdateData(newData);
    } else {
      // Add
      const newItem = { ...values, id: `md_${Date.now()}` };
      onUpdateData([newItem, ...data]);
    }
    setIsModalVisible(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = xlsx.read(bstr, { type: 'binary' });
      const sheetName = wb.SheetNames[0];
      const sheet = wb.Sheets[sheetName];
      const parsedData = xlsx.utils.sheet_to_json(sheet, { defval: null });
      
      const newMasterData = parsedData.map((row, i) => ({
        ...row,
        id: `md_up_${Date.now()}_${i}`
      }));
      
      // Replace existing data per assumption
      onUpdateData(newMasterData);
      setSelectedIds(new Set());
      setCurrentPage(1);
    };
    reader.readAsBinaryString(file);
    // Reset input
    e.target.value = null;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <ChevronUp size={14} color="#d1d5db" style={{ marginLeft: 4 }} />;
    return sortConfig.direction === 'asc' 
      ? <ChevronUp size={14} color="#3b82f6" style={{ marginLeft: 4 }} />
      : <ChevronDown size={14} color="#3b82f6" style={{ marginLeft: 4 }} />;
  };

  const renderSearchInput = (key) => (
    <input 
      type="text" 
      placeholder="Search..."
      value={columnFilters[key] || ''}
      onClick={(e) => e.stopPropagation()} // Prevent triggering sort when clicking the input
      onChange={(e) => {
        setColumnFilters(prev => ({ ...prev, [key]: e.target.value }));
        setCurrentPage(1);
      }}
      style={{
        width: '100%',
        padding: '4px 8px',
        fontSize: '12px',
        border: '1px solid #d1d5db',
        borderRadius: '4px',
        marginTop: '6px',
        outline: 'none',
        fontWeight: 'normal'
      }}
    />
  );

  return (
    <div className="heat-profile-content-wrapper admin-modern-page mobilist-full-width-page">
      <div className="heat-profile-card mdv-page">
        {/* Header */}
        <div className="heat-profile-list-header">
          <div className="heat-profile-header-left">
            <button className="mdv-back-btn" onClick={onBack}>
              <ArrowLeft size={20} color="#059669" />
            </button>
            <div className="heat-profile-header-content">
              <Title level={4} className="heat-profile-list-title">Master Data</Title>
              <p className="heat-profile-list-description">Manage the underlying SAP master list for cost calculations.</p>
            </div>
          </div>
          <div className="heat-profile-header-actions calibration-modern-actions" style={{ gap: '12px' }}>
            {selectedIds.size > 0 && (
              <CrcButton
                label={`Delete Selected (${selectedIds.size})`}
                variant="danger"
                icon={Trash2}
                onClick={handleDeleteSelected}
                size="small"
              />
            )}
            
            <input 
              type="file" 
              accept=".numbers, .xlsx, .xls"
              style={{ display: 'none' }}
              ref={fileInputRef}
              onChange={handleFileUpload}
            />
            <CrcButton
              label="Upload Excel"
              variant="secondary"
              icon={Upload}
              onClick={() => fileInputRef.current.click()}
              size="small"
              className="heat-profile-filter-btn"
            />
            <CrcButton
              label="Add New"
              variant="primary"
              icon={Plus}
              onClick={handleAddNew}
              size="small"
              className="heat-profile-add-btn"
            />
          </div>
        </div>

        {/* Table */}
        <div className="heat-profile-table-wrap">
          <div className="mobilist-table-wrapper">
            <table className="custom-table">
              <thead>
                <tr>
                  <th style={{ width: '40px', textAlign: 'center' }}>
                    <Checkbox 
                      checked={isAllPageSelected}
                      indeterminate={isIndeterminate}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th style={{ cursor: 'pointer', verticalAlign: 'top', minWidth: '250px' }} onClick={() => handleSort('£ KEY')}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>Description {renderSortIcon('£ KEY')}</div>
                    {renderSearchInput('£ KEY')}
                  </th>
                  <th style={{ cursor: 'pointer', verticalAlign: 'top' }} onClick={() => handleSort('__EMPTY')}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>SAP # {renderSortIcon('__EMPTY')}</div>
                    {renderSearchInput('__EMPTY')}
                  </th>
                  <th style={{ cursor: 'pointer', verticalAlign: 'top' }} onClick={() => handleSort('__EMPTY_1')}>
                    <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>Moblist Cost £ {renderSortIcon('__EMPTY_1')}</div>
                    {renderSearchInput('__EMPTY_1')}
                  </th>
                  <th style={{ cursor: 'pointer', verticalAlign: 'top' }} onClick={() => handleSort('__EMPTY_2')}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>HS # {renderSortIcon('__EMPTY_2')}</div>
                    {renderSearchInput('__EMPTY_2')}
                  </th>
                  <th style={{ cursor: 'pointer', verticalAlign: 'top' }} onClick={() => handleSort('__EMPTY_3')}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>WBS # {renderSortIcon('__EMPTY_3')}</div>
                    {renderSearchInput('__EMPTY_3')}
                  </th>
                  <th style={{ cursor: 'pointer', verticalAlign: 'top' }} onClick={() => handleSort('__EMPTY_4')}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>Ret/Con {renderSortIcon('__EMPTY_4')}</div>
                    {renderSearchInput('__EMPTY_4')}
                  </th>
                  <th style={{ cursor: 'pointer', verticalAlign: 'top' }} onClick={() => handleSort('__EMPTY_5')}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>Type {renderSortIcon('__EMPTY_5')}</div>
                    {renderSearchInput('__EMPTY_5')}
                  </th>
                  <th style={{ cursor: 'pointer', verticalAlign: 'top' }} onClick={() => handleSort('__EMPTY_6')}>
                    <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>Get Ready -R {renderSortIcon('__EMPTY_6')}</div>
                    {renderSearchInput('__EMPTY_6')}
                  </th>
                  <th style={{ cursor: 'pointer', verticalAlign: 'top' }} onClick={() => handleSort('__EMPTY_7')}>
                    <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>Purchase -R {renderSortIcon('__EMPTY_7')}</div>
                    {renderSearchInput('__EMPTY_7')}
                  </th>
                  <th style={{ cursor: 'pointer', verticalAlign: 'top' }} onClick={() => handleSort('__EMPTY_8')}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>Owner {renderSortIcon('__EMPTY_8')}</div>
                    {renderSearchInput('__EMPTY_8')}
                  </th>
                  <th style={{ cursor: 'pointer', verticalAlign: 'top' }} onClick={() => handleSort('__EMPTY_9')}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>Category {renderSortIcon('__EMPTY_9')}</div>
                    {renderSearchInput('__EMPTY_9')}
                  </th>
                  <th style={{ cursor: 'pointer', verticalAlign: 'top', minWidth: '200px' }} onClick={() => handleSort('__EMPTY_10')}>
                    <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>Ext. Description {renderSortIcon('__EMPTY_10')}</div>
                    {renderSearchInput('__EMPTY_10')}
                  </th>
                  <th style={{ width: '100px', verticalAlign: 'top' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="14" className="empty-state">
                      <div className="ant-empty ant-empty-normal">
                        <div className="ant-empty-image">
                          {/* Empty SVG placeholder */}
                          <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                            <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                              <ellipse fill="#e2e8f0" cx="32" cy="33" rx="32" ry="7" />
                              <g fillRule="nonzero" stroke="#cbd5e1">
                                <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" fill="#f8fafc" />
                                <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" fill="#f8fafc" />
                              </g>
                            </g>
                          </svg>
                        </div>
                        <div className="ant-empty-description">No data available</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row) => (
                    <tr key={row.id}>
                      <td style={{ textAlign: 'center' }}>
                        <Checkbox 
                          checked={selectedIds.has(row.id)}
                          onChange={() => toggleSelectRow(row.id)}
                        />
                      </td>
                      <td className="wrap-text" style={{ fontWeight: 500, minWidth: '250px' }}>{row['£ KEY'] || '—'}</td>
                      <td>{row['__EMPTY'] || '—'}</td>
                      <td style={{ fontWeight: 600 }}>{formatCurrency(row['__EMPTY_1'])}</td>
                      <td>{row['__EMPTY_2'] || '—'}</td>
                      <td>{row['__EMPTY_3'] || '—'}</td>
                      <td>{row['__EMPTY_4'] || '—'}</td>
                      <td>{row['__EMPTY_5'] || '—'}</td>
                      <td>{formatCurrency(row['__EMPTY_6'])}</td>
                      <td>{formatCurrency(row['__EMPTY_7'])}</td>
                      <td>{row['__EMPTY_8'] || '—'}</td>
                      <td>{row['__EMPTY_9'] || '—'}</td>
                      <td className="wrap-text">{row['__EMPTY_10'] || '—'}</td>
                      <td>
                        <div className="table-actions">
                          <button className="action-btn" title="Edit" onClick={() => handleEditRow(row)}>
                            <Edit2 size={16} />
                          </button>
                          <button className="action-btn" title="Delete" onClick={() => handleDeleteRow(row.id)}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="heat-profile-pagination">
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={processedData.length}
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
              showSizeChanger
              pageSizeOptions={['10', '20', '50']}
              showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
            />
          </div>
        </div>
      </div>
      
      <MasterDataModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSave={handleModalSave}
        initialData={editingItem}
      />
    </div>
  );
};

export default MobilistMasterDataView;
